import { useMemo } from 'react'

import { Link as RouterLink } from 'react-router-dom'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { T } from '@tribeplatform/react-components/i18n'
import { useAuthToken, useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { AuthOperationType } from '../../../server/middlewares/ssos/utils/sso.utils.js'
import { useQuery } from '../../hooks/useQuery.js'
import { getRedirectParamsFromQuery } from '../../utils/auth.utils.js'
import { LoginForm } from './Forms/LoginForm.js'
import { SsoOptions } from './SsosOptions.js'

type LoginProps = {
  /**
   * This prop is populated when accessing **\/auth/login/direct**,
   * so it is either `true` or `undefined`.
   */
  direct?: true
}

export const Login = ({ direct }: LoginProps) => {
  const { data: authToken } = useAuthToken() || {}
  const { network } = authToken
  const { generatePath } = useSpaceUtil()
  const [canJoinNetwork] = hasScopesPermission(network, ['joinNetwork'])

  const query = useQuery()
  const redirectPath = useMemo(() => getRedirectParamsFromQuery(query), [query])

  const showLoginForm = direct ?? !network.hideDefaultAuthenticationForm
  const showSsos = !direct

  return (
    <>
      <div className="mx-auto w-full max-w-md text-center text-content-on-background">
        <Text
          as="h2"
          variant="headingLg"
          align="center"
          onBackground
          className="mt-6"
        >
          <T defaultMessage="Sign in to your account" id="Auth.Login.Title" />
        </Text>
        {canJoinNetwork && (
          <div className="mt-3 text-lg">
            <T
              defaultMessage="Don’t have an account?"
              id="Auth.Login.SignUpInstruct"
            />{' '}
            <Link
              as={RouterLink}
              to={generatePath(SystemSpaceSlug.SIGNUP, null, {
                redirect: redirectPath,
              })}
            >
              <T
                defaultMessage="Sign up now"
                id="Auth.Login.Actions.SignUpLink"
              />
            </Link>
          </div>
        )}
      </div>

      <div className="mt-8 mx-auto w-full max-w-md flex flex-col space-y-5">
        <Card>
          <Card.Content>
            {showLoginForm && <LoginForm redirectPath={redirectPath} />}
            {showSsos && (
              <SsoOptions
                redirectPath={redirectPath}
                operationType={AuthOperationType.login}
              />
            )}
          </Card.Content>
        </Card>
      </div>
    </>
  )
}

// Default exports are needed for React.lazy
// See https://reactjs.org/docs/code-splitting.html#named-exports
// eslint-disable-next-line import/no-default-export
export default Login
